/**
 * FASE 23.3 — CONTEXT BUDGET MANAGER
 *
 * Controls the total size of operational context prefixes injected into
 * every LLM prompt. Prevents context collapse from accumulated state.
 *
 * Limits:
 *  - workflow memory  ≤ 300 chars
 *  - data context     ≤ 500 chars
 *  - runtime context  ≤ 350 chars
 *  - total prefixes   ≤ 1200 chars
 *
 * Truncation priority (what survives when over budget):
 *  1. Entity context (most relevant to current conversation)
 *  2. Data context (live pastoral data)
 *  3. Runtime context (recency/navigation)
 *  4. Workflow context (background state — trimmed last to first)
 */

const LIMITS = Object.freeze({
  workflow: 300,
  data:     500,
  runtime:  350,
  total:    1200,
});

/**
 * Trim a string to a maximum character budget.
 * Tries to cut at a sentence/word boundary.
 */
function trimToBudget(str, budget) {
  if (!str || str.length <= budget) return str ?? "";
  // Try to cut at last newline before limit
  const atNewline = str.lastIndexOf("\n", budget - 3);
  if (atNewline > budget * 0.6) return str.slice(0, atNewline) + "…";
  // Fall back to hard cut
  return str.slice(0, budget - 1) + "…";
}

/**
 * @typedef {Object} ContextBudgetResult
 * @property {string} workflowContext — trimmed workflow memory
 * @property {string} dataContext     — trimmed data context
 * @property {string} runtimeContext  — trimmed runtime context
 * @property {number} totalUsed       — chars consumed by all prefixes
 * @property {boolean} wasConstrained — true if any trimming occurred
 * @property {{ component: string, original: number, trimmed: number }[]} log
 */

/**
 * Shape all context components to fit within budget constraints.
 *
 * @param {{
 *   workflowContext?: string|null,
 *   dataContext?: string|null,
 *   runtimeContext?: string|null,
 * }} components
 * @returns {ContextBudgetResult}
 */
export function buildContextBudget({
  workflowContext = null,
  dataContext     = null,
  runtimeContext  = null,
} = {}) {
  const log  = [];
  let total  = 0;
  let wasConstrained = false;

  function shape(raw, limit, name) {
    const original = raw?.length ?? 0;
    if (!raw) return "";
    const trimmed = trimToBudget(raw, limit);
    if (trimmed.length < original) {
      wasConstrained = true;
      log.push({ component: name, original, trimmed: trimmed.length });
    }
    return trimmed;
  }

  // Shape each component to its individual limit first
  let wf   = shape(workflowContext, LIMITS.workflow, "workflow");
  let data = shape(dataContext,     LIMITS.data,     "data");
  let rt   = shape(runtimeContext,  LIMITS.runtime,  "runtime");

  total = wf.length + data.length + rt.length;

  // If total still exceeds budget, trim in reverse priority order
  if (total > LIMITS.total) {
    const excess = total - LIMITS.total;

    // First trim workflow (lowest priority under the total budget)
    if (wf.length > 0 && excess > 0) {
      const cut  = Math.min(wf.length, excess);
      const prev = wf.length;
      wf         = trimToBudget(wf, wf.length - cut);
      total     -= (prev - wf.length);
      wasConstrained = true;
      log.push({ component: "workflow-total-trim", original: prev, trimmed: wf.length });
    }

    // Then trim runtime
    if (total > LIMITS.total && rt.length > 0) {
      const cut  = Math.min(rt.length, total - LIMITS.total);
      const prev = rt.length;
      rt         = trimToBudget(rt, rt.length - cut);
      total     -= (prev - rt.length);
      wasConstrained = true;
      log.push({ component: "runtime-total-trim", original: prev, trimmed: rt.length });
    }

    // Finally trim data (most important live data — trim last, minimally)
    if (total > LIMITS.total && data.length > 0) {
      const cut  = Math.min(data.length, total - LIMITS.total);
      const prev = data.length;
      data       = trimToBudget(data, data.length - cut);
      total     -= (prev - data.length);
      wasConstrained = true;
      log.push({ component: "data-total-trim", original: prev, trimmed: data.length });
    }
  }

  return {
    workflowContext: wf,
    dataContext:     data,
    runtimeContext:  rt,
    totalUsed:       wf.length + data.length + rt.length,
    wasConstrained,
    log,
  };
}

/** Returns the budget limits for dev inspection. */
export function getContextLimits() {
  return { ...LIMITS };
}
